import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import {
  ContratoEfetivoService,
  EfetivoService,
  EscalaBulkItemDto,
  EscalaEfetivoAddOrUpdateDto,
  EscalaEfetivoBulkDto,
  EscalaService,
  EscalaUpdateDto,
  OcorrenciaService,
  PapelEfetivoEscalaEnum,
  TurnoService,
} from '@core/services/domain-services';
import { Efetivo, Escala, Posto, Turno } from '@core/models/models';
import { EnumOption, EnumService } from '@shared/api/services/enum.service';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { EscalaHistoricoComponent } from '@shared/components/escala-historico/escala-historico.component';
import {
  FaltaCreateDto,
  FaltaService,
  TipoFaltaOption,
} from '@core/services/falta.service';
import { DrawerModule } from 'primeng/drawer';
import {
  EfetivoDoPosto,
  EscalaCalendarioComponent,
  EscalaSlotEvent,
} from './escala-calendario/escala-calendario.component';

interface ApiPage<T> {
  data?: T[];
  records?: T[];
  items?: T[];
  totalRecords?: number;
}

interface PapelEscalaOption {
  key: string;
  value: PapelEfetivoEscalaEnum;
  label: string;
  icon: string;
  descricao: string;
  detalhe: string;
}

interface PlanoEscalaItem extends EscalaBulkItemDto {
  uiKey: string;
}

interface EscalaMembroView {
  efetivoId: number;
  escalaEfetivoId?: number;
  nome: string;
  codigo?: string;
  cargo?: string;
  urlFoto?: string;
  papel: PapelEfetivoEscalaEnum;
  isFalta: boolean;
  faltaId?: number;
  raw?: any;
}

interface FaltaCandidate {
  key: string;
  escala: Escala;
  membro: EscalaMembroView;
  data: Date;
  turnoId: number;
  turnoNome: string;
  periodo: string;
}

interface EstadoEscalaOption {
  label: string;
  value: string;
  icon: string;
}

type FaltaPeriodoMode = 'single' | 'range' | 'week';

interface CategoriaEfetivoInfo {
  idCategoria?: number;
  nomeCategoria?: string;
}

const FOLGA_LOOKBACK_DIAS = 7;
const CATEGORIAS_PAGE_SIZE = 500;

@Component({
  selector: 'app-posto-escala-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DrawerModule,
    SkeletonModule,
    ToastModule,
    TooltipModule,
    SelectModule,
    EscalaCalendarioComponent,
    PaginatorControlComponent,
    EscalaHistoricoComponent,
  ],
  templateUrl: './posto-escala-tab.component.html',
  styleUrl: './posto-escala-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostoEscalaTabComponent implements OnInit, OnChanges {
  @Input() postoId!: number;
  @Input() posto: Posto | null = null;
  @Input() openCreateOnInit = false;

  @Output() countChange = new EventEmitter<number>();
  @Output() createDrawerOpened = new EventEmitter<void>();

  private readonly contratoSvc = inject(ContratoEfetivoService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly escalaSvc = inject(EscalaService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly faltaSvc = inject(FaltaService);
  private readonly turnoSvc = inject(TurnoService);
  private readonly enumSvc = inject(EnumService);
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  semanaInicio = this.getMonday(new Date());
  efetivos: EfetivoDoPosto[] = [];
  escalas: Escala[] = [];
  turnos: Turno[] = [];

  private readonly categoriasPorPosto = new Map<
    number,
    Map<number, CategoriaEfetivoInfo>
  >();

  page = 1;
  pageSize = 10;
  totalEfetivos = 0;

  loadingEfetivos = false;
  loadingEscalas = false;
  loadingTurnos = false;
  loadingPapeis = false;

  showCreateDialog = false;
  createMode: 'single' | 'multiple' = 'multiple';
  createForm!: FormGroup;
  submitting = false;
  planoEscala: PlanoEscalaItem[] = [];
  private readonly planoEfetivos = new Map<number, EfetivoDoPosto>();

  pickerEfetivos: EfetivoDoPosto[] = [];
  pickerSelected = new Map<number, EfetivoDoPosto>();
  pickerPage = 1;
  pickerPageSize = 5;
  pickerTotal = 0;
  pickerSearch = '';
  pickerLoading = false;
  private pickerSearchTimer?: ReturnType<typeof setTimeout>;

  showEditDialog = false;
  showFaltaDrawer = false;
  showRemoverDrawer = false;
  showConflictDrawer = false;
  conflitoSlot: EscalaSlotEvent | null = null;
  editForm!: FormGroup;
  faltaForm!: FormGroup;
  editSubmitting = false;
  removing = false;
  submittingFalta = false;
  loadingFaltaDetalhe = false;
  faltaDetalhe: any = null;
  faltaPeriodoMode: FaltaPeriodoMode = 'single';
  faltaStandalone = false;
  faltaCandidateSearch = '';
  faltaCandidatePage = 1;
  faltaCandidatePageSize = 5;
  removingFalta = false;
  confirmandoRemoverFalta = false;
  loadingTiposFalta = false;
  tiposFalta: TipoFaltaOption[] = [];
  slotEmEdicao: EscalaSlotEvent | null = null;
  escalaEmEdicao: Escala | null = null;
  membrosEscala: EscalaMembroView[] = [];
  membroEmEdicaoId: number | null = null;
  removerSelecionadosIds = new Set<number>();
  membrosPendentesRemocao: EscalaMembroView[] = [];
  historicoEscala: any[] = [];
  loadingHistoricoEscala = false;

  readonly pickerSkeletonRows = [1, 2, 3, 4, 5];
  readonly observacoesRapidas = [
    'Não comunicou previamente',
    'Comunicou ao supervisor',
    'Apresentou justificativo',
    'Sem justificativo apresentado',
    'Substituição acionada',
    'Turno coberto por efetivo de reserva',
  ];

  readonly estadosEscala: EstadoEscalaOption[] = [
    { label: 'Agendada', value: 'Agendada', icon: 'ri-calendar-check-line' },
    { label: 'Em execução', value: 'EmExecucao', icon: 'ri-play-circle-line' },
    { label: 'Concluída', value: 'Concluida', icon: 'ri-checkbox-circle-line' },
    { label: 'Cancelada', value: 'Cancelada', icon: 'ri-close-circle-line' },
  ];

  private readonly papelMeta: Record<
    string,
    { icon: string; descricao: string; detalhe: string }
  > = {
    SUPERVISOR: {
      icon: 'ri-user-star-line',
      descricao: 'Inicia e encerra o turno',
      detalhe:
        'Responsável por iniciar a escala, confirmar presenças, registar ocorrências e encerrar o turno.',
    },
    EFETIVO: {
      icon: 'ri-shield-user-line',
      descricao: 'Executa rondas e checklist',
      detalhe:
        'Executa o serviço normal do turno, rondas, checklist de equipamentos e ocorrências.',
    },
    VIGILANTE: {
      icon: 'ri-eye-line',
      descricao: 'Apoia a operação',
      detalhe:
        'Apoia a cobertura do posto e substitui a liderança operacional quando necessário.',
    },
    RESERVA: {
      icon: 'ri-user-received-2-line',
      descricao: 'Disponível para substituição',
      detalhe:
        'Efetivo de prevenção para cobrir faltas, reforços ou substituições.',
    },
  };

  private readonly papelMetaDefault = {
    icon: 'ri-user-line',
    descricao: 'Papel na escala',
    detalhe: 'Papel atribuído ao efetivo nesta escala.',
  };

  private readonly papeisFallback: PapelEscalaOption[] = [
    {
      key: '2',
      value: 2 as PapelEfetivoEscalaEnum,
      label: 'Supervisor',
      ...this.papelMeta['SUPERVISOR'],
    },
    {
      key: '1',
      value: 1 as PapelEfetivoEscalaEnum,
      label: 'Efetivo',
      ...this.papelMeta['EFETIVO'],
    },
    {
      key: '3',
      value: 3 as PapelEfetivoEscalaEnum,
      label: 'Vigilante',
      ...this.papelMeta['VIGILANTE'],
    },
    {
      key: '4',
      value: 4 as PapelEfetivoEscalaEnum,
      label: 'Reserva',
      ...this.papelMeta['RESERVA'],
    },
  ];

  papeisEscala: PapelEscalaOption[] = this.papeisFallback;
  papelSelecionado: PapelEfetivoEscalaEnum = 1 as PapelEfetivoEscalaEnum;

  ngOnInit(): void {
    this.createForm = this.fb.group({
      turnoId: [null, Validators.required],
      dataInicio: [null, Validators.required],
      dataTermino: [null, Validators.required],
    });

    this.editForm = this.fb.group({
      turnoId: [null, Validators.required],
      dataInicio: ['', Validators.required],
      dataTermino: ['', Validators.required],
      estado: ['Agendada', Validators.required],
      papelEfetivoEscalaEnum: [1, Validators.required],
    });

    this.faltaForm = this.fb.group({
      tipoFaltaEnum: [null, Validators.required],
      dataFaltaInicio: ['', Validators.required],
      dataFaltaFim: ['', Validators.required],
      justificada: [false],
      observacao: ['', Validators.maxLength(500)],
    });

    this.loadTurnos();
    this.loadPapeisEscala();
    this.loadTiposFalta();
    this.loadEfetivos();
    this.loadEscalas();

    if (this.openCreateOnInit) {
      queueMicrotask(() => {
        this.openCreateDialog();
        this.createDrawerOpened.emit();
        this.cdr.markForCheck();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['postoId'] &&
      !changes['postoId'].firstChange &&
      this.createForm
    ) {
      this.page = 1;
      this.pickerPage = 1;
      this.pickerSelected.clear();
      this.planoEscala = [];
      this.planoEfetivos.clear();
      this.showCreateDialog = false;
      this.closeEditDialog();
      this.loadEfetivos();
      this.loadEscalas();
    }
  }

  get loading(): boolean {
    return this.loadingEfetivos || this.loadingEscalas;
  }

  get first(): number {
    return (this.page - 1) * this.pageSize;
  }

  get pickerFirst(): number {
    return (this.pickerPage - 1) * this.pickerPageSize;
  }

  get pickerTotalPages(): number {
    return Math.max(1, Math.ceil(this.pickerTotal / this.pickerPageSize));
  }

  get selectedEfetivos(): EfetivoDoPosto[] {
    return [...this.pickerSelected.values()];
  }

  get selectedTurno(): Turno | null {
    const id = Number(this.createForm?.value.turnoId);
    return (
      this.turnos.find((turno) => Number((turno as any).id) === id) ?? null
    );
  }

  get turnoBloqueadoId(): number | null {
    const efetivoComTurno = this.selectedEfetivos.find(
      (efetivo) => efetivo.turnoOriginal?.id != null,
    );
    const id = Number(efetivoComTurno?.turnoOriginal?.id);
    return Number.isFinite(id) ? id : null;
  }

  get selectedEditTurno(): Turno | null {
    const id = Number(this.editForm?.value.turnoId);
    return (
      this.turnos.find((turno) => Number((turno as any).id) === id) ?? null
    );
  }

  get membroEmEdicao(): EscalaMembroView | null {
    return (
      this.membrosEscala.find(
        (membro) => membro.efetivoId === this.membroEmEdicaoId,
      ) ?? null
    );
  }

  get faltaEmConsulta(): boolean {
    return !!this.membroEmEdicao?.isFalta;
  }

  get escalaTemFalta(): boolean {
    return !!this.membroEmEdicao?.isFalta;
  }

  /**
   * Avalia se a escala já iniciou ou se está num estado que impede alteração/eliminação.
   * Regras:
   * - Futura (ainda não iniciou): Pode editar e eliminar.
   * - Em curso / Concluída / Passada da hora de início: Bloqueia edição e eliminação.
   */
  podeEditarOuEliminarEscala(
    escala: Escala | null,
    dataOcorrencia: Date | null = this.slotEmEdicao?.data ?? null,
  ): boolean {
    if (!escala) return false;
    const value = escala as any;
    const dataInicioBase = this.parseApiDate(
      value.dataInicio ?? value.dataInicioPrevista,
    );
    const dataInicio = this.getEscalaInicioNaOcorrencia(
      escala,
      dataInicioBase,
      dataOcorrencia,
    );
    const inicioAindaFuturo = !!dataInicio && Date.now() < dataInicio.getTime();

    // 1. Verificar pelo estado do objeto
    const estado = this.getEscalaEstado(escala);
    if (estado === 'Concluida') {
      return false;
    }
    if (estado === 'EmExecucao' && !inicioAindaFuturo) return false;

    // 2. Verificar pelo horário de início do turno
    if (dataInicio && new Date() >= dataInicio) {
      return false;
    }

    return true;
  }

  private getEscalaInicioNaOcorrencia(
    escala: Escala,
    inicioBase: Date | null,
    dataOcorrencia: Date | null,
  ): Date | null {
    if (!inicioBase || !dataOcorrencia) return inicioBase;
    if (this.toDateKey(inicioBase) === this.toDateKey(dataOcorrencia)) {
      return inicioBase;
    }

    const value = escala as any;
    const turnoId = Number(value.turnoId ?? value.idTurno ?? value.turno?.id);
    const turno = this.turnos.find((item) => Number((item as any).id) === turnoId);
    const horaInicio = this.onlyTime(
      value.horaInicio ?? (turno as any)?.horaInicio ?? inicioBase,
    );
    return this.composeApiDate(this.toDateKey(dataOcorrencia), horaInicio);
  }

  /**
   * Verifica se o slot/escala em edição inicia no próprio dia de hoje.
   */
  get podeRegistarFaltaNoSlot(): boolean {
    if (!this.slotEmEdicao && !this.escalaEmEdicao) return false;
    const dataSlot =
      this.parseApiDate(this.slotEmEdicao?.data) ??
      this.parseApiDate(
        (this.escalaEmEdicao as any)?.dataInicio ??
          (this.escalaEmEdicao as any)?.dataInicioPrevista,
      );
    if (!dataSlot) return false;

    const hoje = this.startOfDay(new Date());
    const diaEscala = this.startOfDay(dataSlot);

    return hoje.getTime() === diaEscala.getTime();
  }

  get faltaPeriodoLabel(): string {
    const inicio = this.parseDateKey(this.faltaForm?.value.dataFaltaInicio);
    const fim = this.parseDateKey(this.faltaForm?.value.dataFaltaFim);
    if (Number.isNaN(inicio.getTime())) return 'Dia não identificado';
    const format = (date: Date) =>
      date.toLocaleDateString('pt-AO', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    if (this.faltaPeriodoMode === 'single' || Number.isNaN(fim.getTime())) {
      return format(inicio);
    }
    return `${format(inicio)} a ${format(fim)}`;
  }

  get faltaCandidatos(): FaltaCandidate[] {
    const search = this.normalizeText(this.faltaCandidateSearch);
    const unique = new Map<string, FaltaCandidate>();
    const hoje = this.startOfDay(new Date());
    const semanaFim = this.endOfDay(this.addDays(this.semanaInicio, 6));

    this.escalas.forEach((escala) => {
      if (!this.escalaSobrepoePeriodo(escala, this.semanaInicio, semanaFim)) {
        return;
      }

      const escalaId = this.getEscalaId(escala);
      const data =
        this.parseApiDate(
          (escala as any).dataInicio ?? (escala as any).dataInicioPrevista,
        ) ?? this.semanaInicio;

      if (this.startOfDay(data).getTime() !== hoje.getTime()) {
        return;
      }

      const turnoId = this.getEscalaTurnoId(escala);
      const turnoNome = this.getTurnoNome(turnoId);
      const periodo = `${this.formatDateShort(data)} · ${turnoNome}`;

      this.buildMembrosEscala(escala).forEach((membro) => {
        if (membro.isFalta || !membro.escalaEfetivoId) return;
        const key = `${escalaId}-${membro.efetivoId}`;
        const searchable = this.normalizeText(
          `${membro.nome} ${membro.codigo ?? ''} ${membro.cargo ?? ''} ${turnoNome}`,
        );
        if (search && !searchable.includes(search)) return;
        unique.set(key, {
          key,
          escala,
          membro,
          data,
          turnoId,
          turnoNome,
          periodo,
        });
      });
    });

    return [...unique.values()].sort(
      (a, b) =>
        a.data.getTime() - b.data.getTime() ||
        a.membro.nome.localeCompare(b.membro.nome),
    );
  }

  get faltaCandidateFirst(): number {
    return (this.faltaCandidatePage - 1) * this.faltaCandidatePageSize;
  }

  get faltaCandidateTotal(): number {
    return this.faltaCandidatos.length;
  }

  get faltaCandidatosPaginados(): FaltaCandidate[] {
    return this.faltaCandidatos.slice(
      this.faltaCandidateFirst,
      this.faltaCandidateFirst + this.faltaCandidatePageSize,
    );
  }

  get faltaValidationMessage(): string {
    if (!this.membroEmEdicao) return 'Selecione primeiro o efetivo e a escala.';
    if (!this.faltaForm?.value.tipoFaltaEnum) return 'Escolha o tipo de falta.';
    if (
      !this.faltaForm?.value.dataFaltaInicio ||
      !this.faltaForm?.value.dataFaltaFim
    ) {
      return 'Defina o período da ausência.';
    }
    return 'Pronto para registar a falta.';
  }

  get totalAlocacoes(): number {
    return this.planoEscala.reduce(
      (total, item) => total + item.efetivos.length,
      0,
    );
  }

  get minDateForTermino(): Date {
    const dataInicio = this.parseDateKey(this.createForm?.value.dataInicio);
    if (!Number.isNaN(dataInicio.getTime())) return dataInicio;
    return this.startOfDay(new Date());
  }

  get turnoOccupantsForCreate(): EscalaMembroView[] {
    return this.getTurnoOccupantsForCreate();
  }

  loadPapeisEscala(): void {
    this.loadingPapeis = true;
    this.enumSvc
      .getPapelEfetivoEscala()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingPapeis = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (options: EnumOption<string>[]) => {
          if (!options?.length) return;

          this.papeisEscala = options.map((opt) => ({
            key: opt.key,
            value: Number(opt.key) as PapelEfetivoEscalaEnum,
            label: opt.label,
            ...(this.papelMeta[opt.value] ?? this.papelMetaDefault),
          }));
        },
        error: () => {
          this.papeisEscala = this.papeisFallback;
        },
      });
  }

  loadTiposFalta(): void {
    this.loadingTiposFalta = true;
    this.faltaSvc
      .getTipos()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingTiposFalta = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.tiposFalta = Object.entries(response?.data ?? {})
            .map(([value, label]) => ({ value: Number(value), label }))
            .filter((item) => Number.isFinite(item.value))
            .sort((a, b) => a.value - b.value);
        },
        error: () => {
          this.tiposFalta = [];
          this.showError('Não foi possível carregar os tipos de falta.');
        },
      });
  }

  loadEfetivos(): void {
    if (!this.postoId) return;

    this.loadingEfetivos = true;
    this.escalaSvc
      .getEfetivosAlocadosPorPosto(this.postoId, {
        pageNumber: this.page,
        pageSize: this.pageSize,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingEfetivos = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.efetivos = this.mapContratos(this.readData(response));
          this.totalEfetivos = response?.totalRecords ?? this.efetivos.length;
          this.aplicarCategoriasDoPosto();
        },
        error: () => {
          this.efetivos = [];
          this.totalEfetivos = 0;
          this.showError('Não foi possível carregar os efetivos do posto.');
        },
      });
  }

  private aplicarCategoriasDoPosto(): void {
    const postoId = this.postoId;
    if (!postoId || !this.efetivos.length) return;

    const cache = this.categoriasPorPosto.get(postoId);
    const base$ = cache ? of(cache) : this.carregarCategoriasDoPosto(postoId);

    base$
      .pipe(
        catchError(() => of(new Map<number, CategoriaEfetivoInfo>())),
        switchMap((mapa) => this.completarCategoriasDoPosto(mapa)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (mapa) => {
          this.categoriasPorPosto.set(postoId, mapa);
          this.mergeCategorias(mapa);
        },
        error: () => this.mergeCategorias(new Map()),
      });
  }

  private completarCategoriasDoPosto(
    mapa: Map<number, CategoriaEfetivoInfo>,
  ): Observable<Map<number, CategoriaEfetivoInfo>> {
    const faltantes = [
      ...new Set(
        this.efetivos
          .map((efetivo) => Number(efetivo.id))
          .filter((id) => id > 0 && !mapa.has(id)),
      ),
    ];

    if (!faltantes.length) return of(mapa);

    return forkJoin(
      faltantes.map((id) =>
        this.efetivoSvc.getById(id).pipe(
          map((resposta: any) => {
            const efetivo = resposta?.data ?? resposta;
            const info: CategoriaEfetivoInfo = {
              idCategoria: efetivo?.idCategoria,
              nomeCategoria: efetivo?.nomeCategoria,
            };
            return { id, info };
          }),
          catchError(() => of(null)),
        ),
      ),
    ).pipe(
      map((registos) => {
        const completo = new Map(mapa);
        for (const registo of registos) {
          if (registo) completo.set(registo.id, registo.info);
        }
        return completo;
      }),
    );
  }

  private carregarCategoriasDoPosto(
    postoId: number,
  ): Observable<Map<number, CategoriaEfetivoInfo>> {
    const pageSize = CATEGORIAS_PAGE_SIZE;
    const params = { IdPosto: postoId, pageNumber: 1, pageSize };

    return this.efetivoSvc.getAll(params).pipe(
      switchMap((primeira: any) => {
        const primeiraPagina = this.readData<Efetivo>(primeira);
        const totalRegistos = Number(
          primeira?.totalRecords ?? primeiraPagina.length,
        );
        const totalPaginas = Math.ceil(totalRegistos / pageSize);

        if (totalPaginas <= 1) return of(primeiraPagina);

        const restantes = Array.from(
          { length: totalPaginas - 1 },
          (_, index) => index + 2,
        );

        return forkJoin(
          restantes.map((pageNumber) =>
            this.efetivoSvc
              .getAll({ IdPosto: postoId, pageNumber, pageSize })
              .pipe(map((resposta) => this.readData<Efetivo>(resposta))),
          ),
        ).pipe(map((paginas) => [primeiraPagina, ...paginas].flat()));
      }),
      map((efetivos) => this.construirMapaCategorias(efetivos)),
    );
  }

  private construirMapaCategorias(
    efetivos: Efetivo[],
  ): Map<number, CategoriaEfetivoInfo> {
    const mapa = new Map<number, CategoriaEfetivoInfo>();

    for (const efetivo of efetivos) {
      const id = Number(efetivo?.id ?? (efetivo as any)?.efetivoId);
      if (!id) continue;
      mapa.set(id, {
        idCategoria: efetivo?.idCategoria,
        nomeCategoria: efetivo?.nomeCategoria,
      });
    }

    return mapa;
  }

  private mergeCategorias(mapa: Map<number, CategoriaEfetivoInfo>): void {
    let alterou = false;

    this.efetivos = this.efetivos.map((efetivo) => {
      const info = mapa.get(efetivo.id);
      if (!info) return efetivo;

      if (
        efetivo.idCategoria === info.idCategoria &&
        efetivo.nomeCategoria === info.nomeCategoria
      ) {
        return efetivo;
      }

      alterou = true;
      return {
        ...efetivo,
        idCategoria: info.idCategoria,
        nomeCategoria: info.nomeCategoria,
      };
    });

    if (alterou) this.cdr.markForCheck();
  }

  private contarEscalasDaSemana(): number {
    const inicio = this.startOfDay(this.semanaInicio);
    const fim = this.endOfDay(this.addDays(this.semanaInicio, 6));

    return this.escalas.filter((escala) =>
      this.escalaSobrepoePeriodo(escala, inicio, fim),
    ).length;
  }

  loadEscalas(): void {
    if (!this.postoId) return;

    this.loadingEscalas = true;
    const fimSemana = this.endOfDay(this.addDays(this.semanaInicio, 6));
    const inicioHistorico = this.startOfDay(
      this.addDays(this.semanaInicio, -FOLGA_LOOKBACK_DIAS),
    );

    this.escalaSvc
      .getAll({
        PostoId: this.postoId,
        DataInicioAte: this.toApiDateTime(fimSemana),
        DataTerminoDe: this.toApiDateTime(inicioHistorico),
        pageNumber: 1,
        pageSize: 500,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingEscalas = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.escalas = this.readData<Escala>(response);
          this.countChange.emit(this.contarEscalasDaSemana());
        },
        error: () => {
          this.escalas = [];
          this.countChange.emit(0);
          this.showError('Não foi possível carregar as escalas da semana.');
        },
      });
  }

  loadTurnos(): void {
    this.loadingTurnos = true;
    this.turnoSvc
      .getAll()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingTurnos = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.turnos = Array.isArray(response)
            ? response
            : this.readData<Turno>(response);
        },
        error: () => {
          this.turnos = [];
          this.showError('Não foi possível carregar os turnos.');
        },
      });
  }

  onWeekChange(date: Date): void {
    this.semanaInicio = this.getMonday(date);
    this.loadEscalas();
  }

  onPageChange(event: any): void {
    this.page = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.loadEfetivos();
  }

  handleSlotClick(slot: EscalaSlotEvent): void {
    if (slot.escala) {
      if (this.slotTemFalta(slot)) {
        this.handleAbsenceClick(slot);
        return;
      }
      this.openEditDialog(slot);
      return;
    }

    this.openCreateDialog(slot);
  }

  handleRemoveClick(slot: EscalaSlotEvent): void {
    if (!slot.escala) return;

    if (!this.podeEditarOuEliminarEscala(slot.escala, slot.data)) {
      this.showError(
        'Não é possível eliminar este turno porque já está em curso.',
      );
      return;
    }

    this.openEditDialog(slot, true);
    this.removerSelecionadosIds.clear();
    slot.membros.forEach((membro) => {
      if (membro.efetivoId === slot.efetivo.id) {
        const view = this.membrosEscala.find(
          (item) => item.efetivoId === membro.efetivoId,
        );
        if (view) this.removerSelecionadosIds.add(this.getRemovalId(view));
      }
    });
    this.showEditDialog = false;
    this.showRemoverDrawer = true;
    this.cdr.markForCheck();
  }

  handleAbsenceClick(slot: EscalaSlotEvent): void {
    if (!slot.escala) return;
    this.faltaStandalone = false;

    const operacao = slot.cell?.estadoOperacional;
    if (
      operacao === 'presente' ||
      operacao === 'atraso' ||
      operacao === 'concluido' ||
      !this.podeEditarOuEliminarEscala(slot.escala, slot.data)
    ) {
      this.showError(
        'Não é possível marcar falta porque o efetivo já deu entrada neste turno.',
      );
      return;
    }

    this.openEditDialog(slot, true);
    this.showEditDialog = false;

    if (!this.membroEmEdicao?.isFalta && !this.podeRegistarFaltaNoSlot) {
      this.showError(
        'Apenas é possível registar falta no dia previsto para o início do turno.',
      );
      return;
    }

    this.setFaltaPeriodoMode('single');
    this.showFaltaDrawer = true;
    this.faltaDetalhe = null;
    this.confirmandoRemoverFalta = false;
    if (this.membroEmEdicao?.isFalta) this.loadFaltaDetalhe();
    this.cdr.markForCheck();
  }

  openStandaloneFaltaDrawer(): void {
    this.showEditDialog = false;
    this.showRemoverDrawer = false;
    this.faltaStandalone = true;
    this.faltaCandidateSearch = '';
    this.faltaCandidatePage = 1;
    this.faltaDetalhe = null;
    this.confirmandoRemoverFalta = false;
    this.slotEmEdicao = null;
    this.escalaEmEdicao = null;
    this.membrosEscala = [];
    this.membroEmEdicaoId = null;
    this.resetFaltaForm();
    this.showFaltaDrawer = true;
    this.cdr.markForCheck();
  }

  selecionarFaltaCandidate(candidate: FaltaCandidate): void {
    const membros = this.buildMembrosEscala(
      candidate.escala,
      candidate.membro.efetivoId,
    );
    this.escalaEmEdicao = candidate.escala;
    this.membrosEscala = membros;
    this.membroEmEdicaoId = candidate.membro.efetivoId;
    this.slotEmEdicao = {
      efetivo: {
        id: candidate.membro.efetivoId,
        nome: candidate.membro.nome,
        codigo: candidate.membro.codigo,
        cargo: candidate.membro.cargo,
        urlFoto: candidate.membro.urlFoto,
      },
      data: candidate.data,
      escala: candidate.escala,
      escalas: [candidate.escala],
      membros,
    };
    this.resetFaltaForm();
    this.cdr.markForCheck();
  }

  onFaltaCandidateSearch(value: string): void {
    this.faltaCandidateSearch = value;
    this.faltaCandidatePage = 1;
  }

  onFaltaCandidatePageChange(event: any): void {
    this.faltaCandidatePage = (event.page ?? 0) + 1;
    this.faltaCandidatePageSize = event.rows ?? this.faltaCandidatePageSize;
    this.cdr.markForCheck();
  }

  alterarFaltaCandidate(): void {
    if (!this.faltaStandalone) return;
    this.slotEmEdicao = null;
    this.escalaEmEdicao = null;
    this.membrosEscala = [];
    this.membroEmEdicaoId = null;
    this.resetFaltaForm();
    this.cdr.markForCheck();
  }

  closeFaltaDrawer(): void {
    this.showFaltaDrawer = false;
    this.faltaStandalone = false;
    this.faltaCandidateSearch = '';
    this.faltaCandidatePage = 1;
  }

  handleConflictClick(slot: EscalaSlotEvent): void {
    this.conflitoSlot = slot;
    this.showConflictDrawer = true;
    this.cdr.markForCheck();
  }

  editarEscalaConflitante(escala: Escala): void {
    if (!this.conflitoSlot) return;
    const slot: EscalaSlotEvent = {
      ...this.conflitoSlot,
      escala,
      membros: this.conflitoSlot.membros,
    };
    this.showConflictDrawer = false;
    this.openEditDialog(slot);
  }

  getConflitoPeriodo(escala: Escala): string {
    const value = escala as any;
    const inicio = this.parseApiDate(
      value.dataInicio ?? value.dataInicioPrevista,
    );
    const fim = this.parseApiDate(
      value.dataFim ??
        value.dataTermino ??
        value.dataFimPrevista ??
        value.dataTerminoPrevista,
    );
    if (!inicio) return 'Período indisponível';
    const date = inicio.toLocaleDateString('pt-AO', {
      day: '2-digit',
      month: '2-digit',
    });
    const time = (item: Date) =>
      item.toLocaleTimeString('pt-AO', {
        hour: '2-digit',
        minute: '2-digit',
      });
    return `${date} · ${time(inicio)}${fim ? `–${time(fim)}` : ''}`;
  }

  getConflitoMembros(escala: Escala): EscalaMembroView[] {
    return this.buildMembrosEscala(escala);
  }

  loadFaltaDetalhe(): void {
    const faltaId = this.membroEmEdicao?.faltaId;
    if (!faltaId) {
      this.loadingFaltaDetalhe = false;
      this.faltaDetalhe = null;
      return;
    }
    this.loadingFaltaDetalhe = true;
    this.faltaSvc
      .getById(faltaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingFaltaDetalhe = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.faltaDetalhe = response?.data ?? response;
        },
        error: () => {
          this.faltaDetalhe = null;
          this.showError('Não foi possível carregar os detalhes da falta.');
        },
      });
  }

  openCreateDialog(slot?: EscalaSlotEvent): void {
    if (slot?.escala) {
      this.openEditDialog(slot);
      return;
    }

    this.createMode = slot?.efetivo ? 'single' : 'multiple';
    this.pickerSelected.clear();
    this.planoEscala = [];
    this.planoEfetivos.clear();
    this.pickerPage = 1;
    this.pickerSearch = '';
    this.papelSelecionado =
      this.papeisEscala.find((papel) => Number(papel.value) === 1)?.value ??
      this.papeisEscala[0]?.value ??
      (1 as PapelEfetivoEscalaEnum);

    const dataDia = this.startOfDay(slot?.data ?? this.semanaInicio);
    this.createForm.reset({
      turnoId: null,
      dataInicio: this.toDateKey(dataDia),
      dataTermino: this.toDateKey(dataDia),
    });

    if (slot?.efetivo) {
      this.pickerSelected.set(slot.efetivo.id, slot.efetivo);
      this.preselectTurnoForEfetivo(slot.efetivo);
    }

    this.showCreateDialog = true;
    this.loadPickerEfetivos();
  }

  closeCreateDialog(): void {
    this.showCreateDialog = false;
    this.pickerSelected.clear();
    if (this.pickerSearchTimer) clearTimeout(this.pickerSearchTimer);
  }

  openEditDialog(slot: EscalaSlotEvent, allowFaltaContext = false): void {
    if (!slot.escala) return;

    if (!this.podeEditarOuEliminarEscala(slot.escala, slot.data)) {
      this.showError(
        'Não é possível editar este turno porque já foi iniciado.',
      );
      return;
    }

    if (this.slotTemFalta(slot) && !allowFaltaContext) {
      this.showError(
        'Esta escala não pode ser editada enquanto existir uma falta registada.',
      );
      return;
    }

    const escala = slot.escala;
    const inicio =
      this.parseApiDate(
        (escala as any).dataInicio ?? (escala as any).dataInicioPrevista,
      ) ?? slot.data;
    const fim =
      this.parseApiDate(
        (escala as any).dataTermino ??
          (escala as any).dataFim ??
          (escala as any).dataTerminoPrevista ??
          (escala as any).dataFimPrevista,
      ) ?? inicio;

    this.slotEmEdicao = slot;
    this.escalaEmEdicao = escala;
    this.membrosEscala = this.buildMembrosEscala(escala, slot.efetivo.id);
    this.membroEmEdicaoId =
      this.membrosEscala.find((membro) => membro.efetivoId === slot.efetivo.id)
        ?.efetivoId ??
      this.membrosEscala[0]?.efetivoId ??
      slot.efetivo.id;
    this.removerSelecionadosIds.clear();

    this.editForm.reset({
      turnoId: this.getEscalaTurnoId(escala),
      dataInicio: this.toDateKey(inicio),
      dataTermino: this.getEditTerminoDateKey(inicio, fim),
      estado: this.getEscalaEstado(escala),
      papelEfetivoEscalaEnum:
        this.membroEmEdicao?.papel ?? (1 as PapelEfetivoEscalaEnum),
    });
    this.resetFaltaForm();

    this.showEditDialog = true;
    this.loadHistoricoEscala();
    this.cdr.markForCheck();
  }

  closeEditDialog(): void {
    this.showEditDialog = false;
    if (this.showFaltaDrawer || this.showRemoverDrawer) return;
    this.showFaltaDrawer = false;
    this.showRemoverDrawer = false;
    this.slotEmEdicao = null;
    this.escalaEmEdicao = null;
    this.membrosEscala = [];
    this.membroEmEdicaoId = null;
    this.removerSelecionadosIds.clear();
    this.membrosPendentesRemocao = [];
    this.historicoEscala = [];
    this.loadingHistoricoEscala = false;
    this.faltaForm?.reset({
      tipoFaltaEnum: null,
      dataFaltaInicio: '',
      dataFaltaFim: '',
      justificada: false,
      observacao: '',
    });
  }

  setCreateMode(mode: 'single' | 'multiple'): void {
    this.createMode = mode;
    if (mode === 'single' && this.pickerSelected.size > 1) {
      const first = this.selectedEfetivos[0];
      this.pickerSelected.clear();
      if (first) this.pickerSelected.set(first.id, first);
    }
    this.cdr.markForCheck();
  }

  setPapel(papel: PapelEfetivoEscalaEnum): void {
    this.papelSelecionado = papel;
    this.cdr.markForCheck();
  }

  setTurno(turno: Turno): void {
    const turnoId = Number((turno as any).id);
    if (this.turnoBloqueadoId !== null && turnoId !== this.turnoBloqueadoId) {
      return;
    }
    this.createForm.controls['turnoId'].setValue(turnoId);
    this.createForm.controls['turnoId'].markAsTouched();
  }

  setEditTurno(turno: Turno): void {
    this.editForm.controls['turnoId'].setValue(Number((turno as any).id));
    this.editForm.controls['turnoId'].markAsTouched();
  }

  loadPickerEfetivos(): void {
    this.pickerLoading = true;
    this.pickerEfetivos = [];

    this.escalaSvc
      .getEfetivosAlocadosPorPosto(this.postoId, {
        pageNumber: this.pickerPage,
        pageSize: this.pickerPageSize,
        NomeEfetivo: this.pickerSearch.trim() || undefined,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.pickerLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.pickerEfetivos = this.mapContratos(this.readData(response));
          this.pickerTotal =
            response?.totalRecords ?? this.pickerEfetivos.length;
        },
        error: () => {
          this.pickerEfetivos = [];
          this.pickerTotal = 0;
        },
      });
  }

  onPickerSearch(value: string): void {
    const search = value.trim();
    if (search === this.pickerSearch && this.pickerPage === 1) return;
    this.pickerSearch = search;
    this.pickerPage = 1;
    this.loadPickerEfetivos();
  }

  onPickerSearchInput(value: string): void {
    if (this.pickerSearchTimer) clearTimeout(this.pickerSearchTimer);
    this.pickerSearchTimer = setTimeout(() => this.onPickerSearch(value), 350);
  }

  clearPickerSearch(): void {
    if (this.pickerSearchTimer) clearTimeout(this.pickerSearchTimer);
    if (!this.pickerSearch) return;
    this.pickerSearch = '';
    this.pickerPage = 1;
    this.loadPickerEfetivos();
  }

  clearPickerSelection(): void {
    this.pickerSelected.clear();
    this.cdr.markForCheck();
  }

  onPickerPageChange(event: any): void {
    this.pickerPage = (event.page ?? 0) + 1;
    this.pickerPageSize = event.rows ?? this.pickerPageSize;
    this.loadPickerEfetivos();
  }

  isSelected(efetivo: EfetivoDoPosto): boolean {
    return this.pickerSelected.has(efetivo.id);
  }

  toggleEfetivo(efetivo: EfetivoDoPosto): void {
    if (this.pickerSelected.has(efetivo.id)) {
      this.pickerSelected.delete(efetivo.id);
      return;
    }

    if (this.createMode === 'single') {
      this.pickerSelected.clear();
    }
    this.pickerSelected.set(efetivo.id, efetivo);
    this.preselectTurnoForEfetivo(efetivo);
  }

  adicionarAoPlano(): void {
    if (this.createForm.invalid || !this.pickerSelected.size) {
      this.createForm.markAllAsTouched();
      this.showError('Selecione os efetivos, o turno e o período.');
      return;
    }

    const turno = this.selectedTurno;
    if (!turno) return;

    const primeiroDia = this.parseDateKey(this.createForm.value.dataInicio);
    const ultimoDia = this.parseDateKey(this.createForm.value.dataTermino);
    if (
      Number.isNaN(primeiroDia.getTime()) ||
      Number.isNaN(ultimoDia.getTime())
    ) {
      this.showError('Confirme as datas da escala.');
      return;
    }

    if (ultimoDia < primeiroDia) {
      this.showError('O último dia não pode ser anterior ao primeiro.');
      return;
    }

    const horario = this.getTurnoTimeParts(turno);
    const selecionados = this.selectedEfetivos;
    selecionados.forEach((efetivo) =>
      this.planoEfetivos.set(efetivo.id, efetivo),
    );

    const novosItens: PlanoEscalaItem[] = [];
    for (
      let dia = new Date(primeiroDia);
      dia <= ultimoDia;
      dia = this.addDays(dia, 1)
    ) {
      const dataKey = this.toDateKey(dia);
      const inicio = this.composeApiDate(dataKey, horario.inicio);
      let termino = this.composeApiDate(dataKey, horario.fim);

      if (
        this.timeToMinutes(horario.fim) <= this.timeToMinutes(horario.inicio)
      ) {
        termino = this.addDaysKeepingTime(termino, 1);
      }

      const dataInicio = this.toApiDateTime(inicio);
      const dataTermino = this.toApiDateTime(termino);
      const turnoId = Number((turno as any).id);
      const uiKey = `${this.postoId}-${turnoId}-${dataInicio}`;
      const existente = this.planoEscala.find((item) => item.uiKey === uiKey);
      const efetivosDto: EscalaEfetivoBulkDto[] = selecionados.map(
        (efetivo) => ({
          efetivoId: efetivo.id,
          papelEfetivoEscalaEnum: this.papelSelecionado,
        }),
      );

      if (existente) {
        const porEfetivo = new Map(
          existente.efetivos.map((efetivo) => [efetivo.efetivoId, efetivo]),
        );
        efetivosDto.forEach((efetivo) =>
          porEfetivo.set(efetivo.efetivoId, efetivo),
        );
        existente.efetivos = [...porEfetivo.values()];
      } else {
        novosItens.push({
          uiKey,
          idPosto: this.postoId,
          turnoId,
          dataInicio,
          dataTermino,
          efetivos: efetivosDto,
        });
      }
    }

    this.planoEscala = [...this.planoEscala, ...novosItens]
      .map((item) => ({ ...item, efetivos: [...item.efetivos] }))
      .sort((a, b) => a.dataInicio.localeCompare(b.dataInicio));
    this.pickerSelected.clear();
    this.cdr.markForCheck();
  }

  removerEfetivoDoPlano(item: PlanoEscalaItem, efetivoId: number): void {
    this.planoEscala = this.planoEscala
      .map((current) =>
        current.uiKey === item.uiKey
          ? {
              ...current,
              efetivos: current.efetivos.filter(
                (efetivo) => efetivo.efetivoId !== efetivoId,
              ),
            }
          : current,
      )
      .filter((current) => current.efetivos.length);
  }

  removerItemDoPlano(item: PlanoEscalaItem): void {
    this.planoEscala = this.planoEscala.filter(
      (current) => current.uiKey !== item.uiKey,
    );
  }

  limparPlano(): void {
    this.planoEscala = [];
    this.planoEfetivos.clear();
  }

  submitEscala(): void {
    if (!this.planoEscala.length) {
      this.showError('Adicione pelo menos uma alocação ao plano.');
      return;
    }

    const payload: EscalaBulkItemDto[] = this.planoEscala.map((item) => ({
      idPosto: item.idPosto,
      turnoId: item.turnoId,
      dataInicio: item.dataInicio,
      dataTermino: item.dataTermino,
      efetivos: item.efetivos.map((efetivo) => ({ ...efetivo })),
    }));

    this.submitting = true;
    this.escalaSvc
      .createBulk(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.submitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          const totalAlocacoes = this.totalAlocacoes;
          const totalRequisicoes = this.planoEscala.length;
          this.messageService.add({
            severity: 'success',
            summary: 'Escala criada',
            detail: `${totalAlocacoes} alocação(ões) enviada(s) em ${totalRequisicoes} requisição(ões).`,
            life: 3500,
          });
          this.limparPlano();
          this.closeCreateDialog();
          this.loadEscalas();
        },
        error: () => {
          //this.showError('A API não conseguiu criar a escala.');
        },
      });
  }

  selecionarMembroParaEditar(membro: EscalaMembroView): void {
    this.membroEmEdicaoId = membro.efetivoId;
    this.editForm.patchValue({ papelEfetivoEscalaEnum: membro.papel });
    this.resetFaltaForm();
    this.cdr.markForCheck();
  }

  submitEditEscala(): void {
    if (!this.escalaEmEdicao || !this.membroEmEdicao) {
      this.showError('Selecione uma escala e um efetivo para editar.');
      return;
    }

    if (!this.podeEditarOuEliminarEscala(this.escalaEmEdicao)) {
      this.showError(
        'Não é possível editar este turno porque já foi iniciado.',
      );
      return;
    }

    if (this.escalaTemFalta) {
      this.showError(
        'Remova primeiro a falta registada para poder editar esta escala.',
      );
      return;
    }

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      this.showError('Preencha o turno, o período e o papel.');
      return;
    }

    const escalaId = this.getEscalaId(this.escalaEmEdicao);
    const turno = this.selectedEditTurno;
    if (!escalaId || !turno) {
      this.showError('Não foi possível identificar a escala ou o turno.');
      return;
    }

    const periodo = this.buildPeriodoFromForm(this.editForm.value, turno);
    if (!periodo) {
      this.showError('O período da escala está inválido.');
      return;
    }

    const membro = this.membroEmEdicao;
    const efetivoDto: EscalaEfetivoAddOrUpdateDto = {
      efetivoId: membro.efetivoId,
      papelEfetivoEscalaEnum: this.editForm.value
        .papelEfetivoEscalaEnum as PapelEfetivoEscalaEnum,
    };

    const payload: EscalaUpdateDto = {
      postoId: this.getEscalaPostoId(this.escalaEmEdicao),
      turnoId: Number(this.editForm.value.turnoId),
      dataInicio: periodo.dataInicio,
      dataTermino: periodo.dataTermino,
      estado: this.mapEstadoToApi(this.editForm.value.estado),
      escalasEfetivosAddOrUpdate: [efetivoDto],
    };

    this.editSubmitting = true;
    this.escalaSvc
      .updateEscala(escalaId, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.editSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Escala atualizada',
            detail: `${membro.nome} atualizado no turno.`,
            life: 3500,
          });
          this.closeEditDialog();
          this.loadEscalas();
        },
        error: () => {
          //this.showError('A API não conseguiu atualizar a escala.');
        },
      });
  }

  toggleMembroRemocao(membro: EscalaMembroView): void {
    const id = this.getRemovalId(membro);
    if (this.removerSelecionadosIds.has(id)) {
      this.removerSelecionadosIds.delete(id);
    } else {
      this.removerSelecionadosIds.add(id);
    }
    this.cdr.markForCheck();
  }

  isMembroSelecionadoParaRemocao(membro: EscalaMembroView): boolean {
    return this.removerSelecionadosIds.has(this.getRemovalId(membro));
  }

  get todosMembrosSelecionadosParaRemocao(): boolean {
    return (
      !!this.membrosEscala.length &&
      this.removerSelecionadosIds.size === this.membrosEscala.length
    );
  }

  toggleTodosMembrosRemocao(): void {
    if (this.todosMembrosSelecionadosParaRemocao) {
      this.removerSelecionadosIds.clear();
    } else {
      this.removerSelecionadosIds = new Set(
        this.membrosEscala.map((membro) => this.getRemovalId(membro)),
      );
    }
    this.cdr.markForCheck();
  }

  confirmarRemoverMembro(membro: EscalaMembroView): void {
    this.confirmarRemoverMembros([membro]);
  }

  confirmarRemoverSelecionados(): void {
    const membros = this.membrosEscala.filter((membro) =>
      this.removerSelecionadosIds.has(this.getRemovalId(membro)),
    );
    this.confirmarRemoverMembros(membros);
  }

  private confirmarRemoverMembros(membros: EscalaMembroView[]): void {
    if (!membros.length || !this.escalaEmEdicao) return;

    if (!this.podeEditarOuEliminarEscala(this.escalaEmEdicao)) {
      this.showError(
        'Não é possível eliminar este turno porque já está em curso.',
      );
      return;
    }

    this.membrosPendentesRemocao = membros;
    this.showRemoverDrawer = true;
    this.cdr.markForCheck();
  }

  confirmarRemocaoNoDrawer(): void {
    const selecionados = this.membrosPendentesRemocao.length
      ? this.membrosPendentesRemocao
      : this.membrosEscala.filter((membro) =>
          this.removerSelecionadosIds.has(this.getRemovalId(membro)),
        );
    const ids = [
      ...new Set(selecionados.map((membro) => this.getRemovalId(membro))),
    ];
    if (!ids.length) return;
    this.removerEfetivosDaEscala(ids);
  }

  abrirDrawerFalta(): void {
    if (!this.membroEmEdicao) return;
    this.faltaStandalone = false;

    if (!this.membroEmEdicao.isFalta && !this.podeRegistarFaltaNoSlot) {
      this.showError(
        'Apenas é possível registar falta no dia previsto para o início do turno.',
      );
      return;
    }

    if (this.membroEmEdicao.isFalta) {
      this.faltaDetalhe = null;
      this.loadFaltaDetalhe();
    } else {
      this.setFaltaPeriodoMode('single');
    }
    this.showFaltaDrawer = true;
  }

  setFaltaPeriodoMode(mode: FaltaPeriodoMode): void {
    this.faltaPeriodoMode = mode;
    const diaClicado = this.startOfDay(this.slotEmEdicao?.data ?? new Date());
    let inicio = diaClicado;
    let fim = diaClicado;

    if (mode === 'week') {
      inicio = this.getMonday(diaClicado);
      fim = this.addDays(inicio, 6);
    }

    this.faltaForm.patchValue({
      dataFaltaInicio: this.toDateKey(inicio),
      dataFaltaFim: this.toDateKey(fim),
    });
    this.cdr.markForCheck();
  }

  abrirDrawerRemocao(): void {
    if (!this.podeEditarOuEliminarEscala(this.escalaEmEdicao)) {
      this.showError(
        'Não é possível eliminar este turno porque já está em curso.',
      );
      return;
    }

    this.removerSelecionadosIds.clear();
    this.membrosPendentesRemocao = [];
    this.showRemoverDrawer = true;
  }

  private removerEfetivosDaEscala(removerEfetivosEscalaIds: number[]): void {
    if (!this.escalaEmEdicao) return;

    if (!this.podeEditarOuEliminarEscala(this.escalaEmEdicao)) {
      this.showError(
        'Não é possível eliminar este turno porque já está em curso.',
      );
      return;
    }

    const escalaId = this.getEscalaId(this.escalaEmEdicao);
    if (!escalaId) {
      this.showError('Não foi possível identificar a escala.');
      return;
    }

    this.removing = true;
    this.escalaSvc
      .removerEfetivosEscala(escalaId, removerEfetivosEscalaIds)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.removing = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          const removidos = new Set(removerEfetivosEscalaIds);
          this.membrosEscala = this.membrosEscala.filter(
            (membro) => !removidos.has(this.getRemovalId(membro)),
          );
          this.removerSelecionadosIds.clear();
          this.membrosPendentesRemocao = [];
          this.showRemoverDrawer = false;
          this.membroEmEdicaoId =
            this.membrosEscala[0]?.efetivoId ?? this.membroEmEdicaoId;

          this.messageService.add({
            severity: 'success',
            summary: 'Efetivo removido',
            detail:
              removerEfetivosEscalaIds.length > 1
                ? 'Efetivos removidos da escala.'
                : 'Efetivo removido da escala.',
            life: 3500,
          });

          if (!this.membrosEscala.length) this.closeEditDialog();
          this.loadEscalas();
        },
        error: () => {
          this.showError('Não foi possível remover o efetivo da escala.');
        },
      });
  }

  selecionarTipoFalta(tipo: TipoFaltaOption): void {
    this.faltaForm.patchValue({ tipoFaltaEnum: tipo.value });
    this.faltaForm.controls['tipoFaltaEnum'].markAsTouched();
  }

  getTipoFaltaIcon(tipo: TipoFaltaOption): string {
    const label = this.normalizeText(tipo.label);
    if (label.includes('ATRAS')) return 'ri-timer-flash-line';
    if (label.includes('SAIDA')) return 'ri-logout-box-r-line';
    if (label.includes('ABANDONO')) return 'ri-walk-line';
    if (label.includes('LICEN')) return 'ri-file-user-line';
    if (label.includes('DOEN')) return 'ri-first-aid-kit-line';
    if (label.includes('OUTRO')) return 'ri-more-line';
    return 'ri-user-unfollow-line';
  }

  getTipoFaltaLabel(value: unknown): string {
    const id = Number(value);
    return (
      this.tiposFalta.find((tipo) => tipo.value === id)?.label ?? `Tipo #${id}`
    );
  }

  toggleObservacaoRapida(texto: string): void {
    const atual = String(this.faltaForm.value.observacao ?? '').trim();
    const partes = atual
      .split(/\.\s+/)
      .map((item) => item.replace(/\.$/, '').trim())
      .filter(Boolean);
    const index = partes.findIndex(
      (item) => this.normalizeText(item) === this.normalizeText(texto),
    );
    if (index >= 0) partes.splice(index, 1);
    else partes.push(texto);
    this.faltaForm.patchValue({
      observacao: partes.length ? `${partes.join('. ')}.` : '',
    });
    this.faltaForm.controls['observacao'].markAsDirty();
  }

  isObservacaoRapidaAtiva(texto: string): boolean {
    return this.normalizeText(this.faltaForm.value.observacao).includes(
      this.normalizeText(texto),
    );
  }

  removerFalta(): void {
    const faltaId = this.membroEmEdicao?.faltaId;
    if (!faltaId) return;
    if (!this.confirmandoRemoverFalta) {
      this.confirmandoRemoverFalta = true;
      return;
    }
    this.removingFalta = true;
    this.faltaSvc
      .remove(faltaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.removingFalta = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Falta removida',
            detail: 'O registo de falta foi removido com sucesso.',
            life: 3500,
          });
          this.showFaltaDrawer = false;
          this.confirmandoRemoverFalta = false;
          this.loadEscalas();
        },
        error: (error) => {
          this.confirmandoRemoverFalta = false;
          this.showError(
            error?.error?.mensagem ?? 'Não foi possível remover a falta.',
          );
        },
      });
  }

  registrarFalta(): void {
    if (!this.escalaEmEdicao || !this.membroEmEdicao) {
      this.showError('Selecione a escala e o efetivo para marcar a falta.');
      return;
    }

    if (this.membroEmEdicao.isFalta) {
      this.showError('Este efetivo já tem uma falta registada nesta escala.');
      return;
    }

    if (!this.podeRegistarFaltaNoSlot) {
      this.showError(
        'Apenas é possível registar falta no próprio dia previsto para o início do turno.',
      );
      return;
    }

    if (this.faltaForm.invalid) {
      this.faltaForm.markAllAsTouched();
      this.showError('Preencha o tipo e o período da falta.');
      return;
    }

    const membro = this.membroEmEdicao;
    const escalaEfetivoId = membro.escalaEfetivoId;
    if (!escalaEfetivoId) {
      this.showError(
        'Não foi possível identificar a alocação do efetivo nesta escala.',
      );
      return;
    }

    const inicio = String(this.faltaForm.value.dataFaltaInicio ?? '');
    const fim = String(this.faltaForm.value.dataFaltaFim ?? '');
    if (fim < inicio) {
      this.showError('A data final não pode ser anterior à data inicial.');
      return;
    }

    const payload: FaltaCreateDto = {
      escalaEfetivoId,
      dataFaltaInicio: inicio,
      dataFaltaFim: fim,
      justificada: !!this.faltaForm.value.justificada,
      tipoFaltaEnum: Number(this.faltaForm.value.tipoFaltaEnum),
      observacao: String(this.faltaForm.value.observacao ?? '').trim(),
    };

    this.submittingFalta = true;
    this.faltaSvc
      .create(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.submittingFalta = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Falta registada',
            detail:
              this.faltaPeriodoMode === 'single'
                ? `${membro.nome} foi marcado como ausente neste dia.`
                : `${membro.nome} foi marcado como ausente no período selecionado.`,
            life: 3500,
          });
          this.resetFaltaForm();
          this.showFaltaDrawer = false;
          this.loadHistoricoEscala();
          this.loadEscalas();
        },
        error: () => {
          this.showError('Não foi possível registar a falta nesta escala.');
        },
      });
  }

  loadHistoricoEscala(): void {
    const escalaId = this.escalaEmEdicao
      ? this.getEscalaId(this.escalaEmEdicao)
      : 0;
    if (!escalaId) {
      this.historicoEscala = [];
      return;
    }

    this.loadingHistoricoEscala = true;
    this.ocorrenciaSvc
      .getAll({
        idEscala: escalaId,
        IdEscala: escalaId,
        pageNumber: 1,
        pageSize: 8,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingHistoricoEscala = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.historicoEscala = this.readData(response);
        },
        error: () => {
          this.historicoEscala = [];
        },
      });
  }

  adicionarEfetivosAoTurnoAtual(): void {
    const value = this.editForm.value;
    this.closeEditDialog();
    this.openCreateDialog();

    this.createForm.patchValue({
      turnoId: value.turnoId,
      dataInicio: this.parseDateKey(value.dataInicio),
      dataTermino: this.parseDateKey(value.dataTermino),
    });
    this.cdr.markForCheck();
  }

  isEfetivoPlaneado(efetivoId: number): boolean {
    return this.planoEscala.some((item) =>
      item.efetivos.some((efetivo) => efetivo.efetivoId === efetivoId),
    );
  }

  isEfetivoJaNoTurnoSelecionado(efetivoId: number): boolean {
    return this.turnoOccupantsForCreate.some(
      (membro) => membro.efetivoId === efetivoId,
    );
  }

  getEfetivoPlanoResumo(efetivoId: number): string {
    const itens = this.planoEscala.filter((item) =>
      item.efetivos.some((efetivo) => efetivo.efetivoId === efetivoId),
    );
    if (!itens.length) return '';

    const turnos = [
      ...new Set(itens.map((item) => this.getTurnoNome(item.turnoId))),
    ];
    return `${turnos.join(', ')} - ${itens.length} ${
      itens.length === 1 ? 'alocação' : 'alocações'
    }`;
  }

  getEfetivoExistenteResumo(efetivoId: number): string {
    const membro = this.turnoOccupantsForCreate.find(
      (item) => item.efetivoId === efetivoId,
    );
    return membro
      ? `${this.getPapelLabel(membro.papel)} já neste turno`
      : 'Já escalado neste turno';
  }

  getEfetivoNome(efetivoId: number): string {
    const efetivo =
      this.planoEfetivos.get(efetivoId) ??
      this.pickerSelected.get(efetivoId) ??
      this.pickerEfetivos.find((item) => item.id === efetivoId) ??
      this.efetivos.find((item) => item.id === efetivoId);
    return efetivo?.nome ?? `Efetivo #${efetivoId}`;
  }

  getEfetivoFoto(efetivoId: number): string | undefined {
    return (
      this.planoEfetivos.get(efetivoId)?.urlFoto ??
      this.efetivos.find((item) => item.id === efetivoId)?.urlFoto
    );
  }

  getEfetivoIniciais(efetivoId: number): string {
    return this.getInitials(this.getEfetivoNome(efetivoId));
  }

  getInitials(nome?: string): string {
    const parts = String(nome ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return '?';
    return parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }

  getTurnoNome(turnoId: number): string {
    const turno = this.turnos.find(
      (item) => Number((item as any).id) === Number(turnoId),
    ) as any;
    return turno?.designacao ?? turno?.nome ?? `Turno #${turnoId}`;
  }

  getTurnoHorario(turno: Turno): string {
    const parts = this.getTurnoTimeParts(turno);
    return `${parts.inicio} - ${parts.fim}`;
  }

  getTurnoIcon(turno: Turno): string {
    const nome = this.normalizeText(
      (turno as any).nome ?? (turno as any).designacao ?? '',
    );
    if (nome.includes('NOTUR')) return 'ri-moon-line';
    if (nome.includes('24')) return 'ri-24-hours-line';
    if (nome.includes('FOLGA')) return 'ri-umbrella-line';
    return 'ri-sun-line';
  }

  getTurnoIconById(turnoId: number): string {
    const turno = this.turnos.find(
      (item) => Number((item as any).id) === Number(turnoId),
    );
    return turno ? this.getTurnoIcon(turno) : 'ri-time-line';
  }

  getPapelLabel(value: PapelEfetivoEscalaEnum | string | number): string {
    const numericVal = this.normalizePapel(value);
    return (
      this.papeisEscala.find((papel) => papel.value === numericVal)?.label ??
      String(value)
    );
  }

  getPapelIcon(value: PapelEfetivoEscalaEnum | string | number): string {
    const numericVal = this.normalizePapel(value);
    return (
      this.papeisEscala.find((papel) => papel.value === numericVal)?.icon ??
      'ri-user-line'
    );
  }

  getEscalaEditTitulo(): string {
    if (!this.escalaEmEdicao) return 'Editar escala';
    const date =
      this.parseApiDate(
        (this.escalaEmEdicao as any).dataInicio ??
          (this.escalaEmEdicao as any).dataInicioPrevista,
      ) ?? new Date();
    return `${this.getTurnoNome(this.getEscalaTurnoId(this.escalaEmEdicao))} - ${this.formatDateShort(date)}`;
  }

  get escalaEmEdicaoTurnoIcon(): string {
    return this.escalaEmEdicao
      ? this.getTurnoIconById(this.getEscalaTurnoId(this.escalaEmEdicao))
      : 'ri-time-line';
  }

  getEscalaDiaResumo(): string {
    if (!this.escalaEmEdicao) return 'Escala selecionada';
    const data =
      this.parseApiDate(
        (this.escalaEmEdicao as any).dataInicio ??
          (this.escalaEmEdicao as any).dataInicioPrevista,
      ) ??
      this.slotEmEdicao?.data ??
      new Date();
    return new Intl.DateTimeFormat('pt-AO', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(data);
  }

  formatPlanoData(item: PlanoEscalaItem): string {
    return new Intl.DateTimeFormat('pt-AO', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }).format(new Date(item.dataInicio));
  }

  formatPlanoHorario(item: PlanoEscalaItem): string {
    return `${item.dataInicio.slice(11, 16)} - ${item.dataTermino.slice(11, 16)}`;
  }

  getHistoricoTitulo(item: any): string {
    const tipo = String(item?.tipoOcorrencia ?? item?.tipo ?? 'Ocorrência')
      .replace(/_/g, ' ')
      .trim();
    if (this.normalizeText(tipo) === 'AUSENCIA') return 'Falta registada';
    return tipo || 'Ocorrência';
  }

  getHistoricoDescricao(item: any): string {
    return String(
      item?.descricao ?? item?.motivo ?? item?.detalhes ?? 'Sem descrição.',
    );
  }

  getHistoricoData(item: any): string {
    const date = this.parseApiDate(
      item?.dataOcorrencia ?? item?.dataRegisto ?? item?.dataCriacao,
    );
    if (!date) return '--';
    return new Intl.DateTimeFormat('pt-AO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  getHistoricoCriticidadeClass(value: unknown): string {
    return this.normalizeText(value || 'normal').toLowerCase();
  }

  trackPlano(_: number, item: PlanoEscalaItem): string {
    return item.uiKey;
  }

  trackMembro(_: number, membro: EscalaMembroView): number {
    return membro.efetivoId;
  }

  private mapContratos(contratos: any[]): EfetivoDoPosto[] {
    const unique = new Map<number, EfetivoDoPosto>();

    for (const contrato of contratos) {
      const id = Number(
        contrato.idEfetivo ?? contrato.efetivo?.id ?? contrato.efetivoId,
      );
      if (!id || unique.has(id)) continue;

      unique.set(id, {
        id,
        nome:
          contrato.nomeEfetivo ?? contrato.efetivo?.nome ?? `Efetivo #${id}`,
        codigo:
          contrato.codigoEfetivo ??
          contrato.efetivo?.codigo ??
          contrato.numeroContrato,
        cargo:
          contrato.cargoEfetivo ??
          contrato.efetivo?.cargo?.nome ??
          contrato.efetivo?.cargo,
        urlFoto:
          contrato.urlFoto ??
          contrato.efetivo?.urlFoto ??
          contrato.efetivo?.urlFotografia,
        idCategoria: contrato.idCategoria ?? contrato.efetivo?.idCategoria,
        nomeCategoria:
          contrato.nomeCategoria ?? contrato.efetivo?.nomeCategoria,
        turnoOriginal: contrato.turno,
      });
    }

    return [...unique.values()];
  }

  private readData<T = any>(response: ApiPage<T> | any): T[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.records)) return response.records;
    if (Array.isArray(response?.items)) return response.items;
    return [];
  }

  private preselectTurnoForEfetivo(efetivo: EfetivoDoPosto): void {
    if (efetivo?.turnoOriginal) {
      const match = this.turnos.find(
        (turno) =>
          Number((turno as any).id) === Number(efetivo.turnoOriginal.id) ||
          this.normalizeText(
            (turno as any).nome ?? (turno as any).designacao,
          ) === this.normalizeText(efetivo.turnoOriginal.designacao),
      );
      if (match) {
        this.createForm.patchValue({ turnoId: (match as any).id });
      }
    }
  }

  private getTurnoOccupantsForCreate(): EscalaMembroView[] {
    if (!this.createForm?.value.turnoId || !this.createForm.value.dataInicio) {
      return [];
    }

    const turnoId = Number(this.createForm.value.turnoId);
    const inicio = this.parseDateKey(this.createForm.value.dataInicio);
    const termino = this.parseDateKey(
      this.createForm.value.dataTermino || this.createForm.value.dataInicio,
    );
    if (Number.isNaN(inicio.getTime()) || Number.isNaN(termino.getTime())) {
      return [];
    }

    const unique = new Map<number, EscalaMembroView>();
    this.escalas
      .filter((escala) => this.getEscalaTurnoId(escala) === turnoId)
      .filter((escala) => this.escalaSobrepoePeriodo(escala, inicio, termino))
      .forEach((escala) => {
        this.buildMembrosEscala(escala).forEach((membro) => {
          if (!unique.has(membro.efetivoId))
            unique.set(membro.efetivoId, membro);
        });
      });

    return [...unique.values()];
  }

  private buildMembrosEscala(
    escala: Escala | null,
    focusEfetivoId?: number,
  ): EscalaMembroView[] {
    if (!escala) return [];

    const value = escala as any;
    const source = this.getEscalaMembersSource(value);
    const membros: EscalaMembroView[] = [];

    if (!source.length) {
      const efetivo = value.efetivo ?? {};
      const efetivoId = Number(
        value.idEfetivo ?? value.efetivoId ?? efetivo.id,
      );
      if (efetivoId) {
        membros.push({
          efetivoId,
          escalaEfetivoId: this.toOptionalNumber(
            value.idEscalaEfetivo ??
              value.escalaEfetivoId ??
              value.efetivoEscalaId ??
              value.idEfetivoEscala,
          ),
          nome: String(
            value.nomeEfetivo ?? efetivo.nome ?? `Efetivo #${efetivoId}`,
          ),
          codigo: value.codigoEfetivo ?? efetivo.codigo,
          cargo: value.cargoEfetivo ?? efetivo.cargo,
          urlFoto: value.urlFoto ?? efetivo.urlFoto ?? efetivo.urlFotografia,
          papel: this.normalizePapel(
            value.papelEfetivoEscalaEnum ??
              value.papel ??
              value.papelEfetivoEscala,
          ),
          isFalta: this.readFaltaFlag(value),
          faltaId: this.toOptionalNumber(value.faltaId ?? value.idFalta),
          raw: value,
        });
      }
    } else {
      source.forEach((item: any) => {
        const efetivo = item.efetivo ?? item;
        const efetivoId = Number(
          item.efetivoId ?? item.idEfetivo ?? efetivo.id,
        );
        if (!efetivoId) return;

        membros.push({
          efetivoId,
          escalaEfetivoId: this.toOptionalNumber(
            item.idEscalaEfetivo ??
              item.escalaEfetivoId ??
              item.efetivoEscalaId ??
              item.idEfetivoEscala,
          ),
          nome: String(
            item.nomeEfetivo ??
              efetivo.nome ??
              item.nome ??
              `Efetivo #${efetivoId}`,
          ),
          codigo: item.codigoEfetivo ?? efetivo.codigo ?? item.codigo,
          cargo: item.cargoEfetivo ?? efetivo.cargo ?? item.cargo,
          urlFoto: item.urlFoto ?? efetivo.urlFoto ?? efetivo.urlFotografia,
          papel: this.normalizePapel(
            item.papelEfetivoEscalaEnum ??
              item.papel ??
              item.papelEfetivoEscala,
          ),
          isFalta: this.readFaltaFlag(item),
          faltaId: this.toOptionalNumber(item.faltaId ?? item.idFalta),
          raw: item,
        });
      });
    }

    if (!membros.length && focusEfetivoId) {
      const efetivo = this.efetivos.find((item) => item.id === focusEfetivoId);
      membros.push({
        efetivoId: focusEfetivoId,
        nome: efetivo?.nome ?? `Efetivo #${focusEfetivoId}`,
        codigo: efetivo?.codigo,
        cargo: efetivo?.cargo,
        urlFoto: efetivo?.urlFoto,
        papel: this.normalizePapel(
          value.papelEfetivoEscalaEnum ??
            value.papel ??
            value.papelEfetivoEscala,
        ),
        isFalta: this.readFaltaFlag(value),
        faltaId: this.toOptionalNumber(value.faltaId ?? value.idFalta),
        raw: value,
      });
    }

    const unique = new Map<number, EscalaMembroView>();
    membros.forEach((membro) => unique.set(membro.efetivoId, membro));

    return [...unique.values()].sort((a, b) => {
      if (a.efetivoId === focusEfetivoId) return -1;
      if (b.efetivoId === focusEfetivoId) return 1;
      return a.nome.localeCompare(b.nome);
    });
  }

  private getEscalaMembersSource(value: any): any[] {
    if (Array.isArray(value.efetivos)) return value.efetivos;
    if (Array.isArray(value.escalaEfetivos)) return value.escalaEfetivos;
    if (Array.isArray(value.efetivosEscala)) return value.efetivosEscala;
    if (Array.isArray(value.escalasEfetivos)) return value.escalasEfetivos;
    return [];
  }

  private buildPeriodoFromForm(
    formValue: any,
    turno: Turno,
  ): { dataInicio: string; dataTermino: string } | null {
    const dataInicioKey = this.normalizeDateKey(formValue.dataInicio);
    const dataTerminoKey =
      this.normalizeDateKey(formValue.dataTermino) ?? dataInicioKey;
    if (!dataInicioKey || !dataTerminoKey) return null;

    const horario = this.getTurnoTimeParts(turno);
    const inicioDia = this.parseDateKey(dataInicioKey);
    const terminoDia = this.parseDateKey(dataTerminoKey);
    const inicio = this.composeApiDate(dataInicioKey, horario.inicio);
    let termino = this.composeApiDate(dataTerminoKey, horario.fim);

    if (
      Number.isNaN(inicioDia.getTime()) ||
      Number.isNaN(terminoDia.getTime()) ||
      Number.isNaN(inicio.getTime()) ||
      Number.isNaN(termino.getTime()) ||
      terminoDia < inicioDia
    ) {
      return null;
    }

    if (termino <= inicio) {
      termino = this.addDaysKeepingTime(termino, 1);
    }

    return {
      dataInicio: this.toApiDateTime(inicio),
      dataTermino: this.toApiDateTime(termino),
    };
  }

  private getTurnoTimeParts(turno: Turno): { inicio: string; fim: string } {
    const value = turno as any;
    const nome = this.normalizeText(value.nome ?? value.designacao ?? '');
    const fallback = nome.includes('NOTUR')
      ? { inicio: '18:00', fim: '06:00' }
      : nome.includes('24')
        ? { inicio: '06:00', fim: '06:00' }
        : nome.includes('FOLGA')
          ? { inicio: '00:00', fim: '23:59' }
          : { inicio: '06:00', fim: '18:00' };

    return {
      inicio: this.onlyTime(value.horaInicio ?? fallback.inicio),
      fim: this.onlyTime(value.horaFim ?? value.horaTermino ?? fallback.fim),
    };
  }

  private resetFaltaForm(): void {
    if (!this.faltaForm) return;
    const escala = this.escalaEmEdicao as any;
    const data =
      this.parseApiDate(this.slotEmEdicao?.data) ??
      this.parseApiDate(escala?.dataInicio ?? escala?.dataInicioPrevista) ??
      new Date();
    const dateKey = this.toDateKey(data);
    this.faltaPeriodoMode = 'single';
    this.faltaForm.reset({
      tipoFaltaEnum: null,
      dataFaltaInicio: dateKey,
      dataFaltaFim: dateKey,
      justificada: false,
      observacao: '',
    });
  }

  private getEscalaId(escala: Escala): number {
    return Number(
      (escala as any).id ??
        (escala as any).idEscala ??
        (escala as any).escalaId ??
        0,
    );
  }

  private getEscalaPostoId(escala: Escala): number {
    return Number(
      (escala as any).postoId ??
        (escala as any).idPosto ??
        (escala as any).posto?.id ??
        this.postoId,
    );
  }

  private getEscalaTurnoId(escala: Escala): number {
    return Number((escala as any).turnoId ?? (escala as any).idTurno ?? 0);
  }

  private getEscalaEstado(escala: Escala): string {
    const raw =
      (escala as any).estado ?? (escala as any).estadoEscala ?? 'Agendada';
    const numeric = Number(raw);
    if (!Number.isNaN(numeric)) {
      return (
        this.estadosEscala[numeric]?.value ??
        this.estadosEscala.find((item) => item.value === 'Agendada')?.value ??
        'Agendada'
      );
    }
    return String(raw);
  }

  private mapEstadoToApi(value: string): number {
    const map: Record<string, number> = {
      Agendada: 0,
      EmExecucao: 1,
      Concluida: 2,
      Cancelada: 3,
    };
    return map[value] ?? 0;
  }

  private getEditTerminoDateKey(inicio: Date, fim: Date): string {
    const duracaoHoras = (fim.getTime() - inicio.getTime()) / 3_600_000;
    if (duracaoHoras > 0 && duracaoHoras <= 26) {
      return this.toDateKey(inicio);
    }
    return this.toDateKey(fim);
  }

  private getRemovalId(membro: EscalaMembroView): number {
    return membro.escalaEfetivoId ?? membro.efetivoId;
  }

  private readFaltaFlag(value: any): boolean {
    return value?.isFalta === true || value?.hasFalta === true;
  }

  private slotTemFalta(slot: EscalaSlotEvent): boolean {
    const membro = slot.membros.find(
      (item) => item.efetivoId === slot.efetivo.id,
    );
    return membro?.isFalta === true || this.readFaltaFlag(slot.escala);
  }

  private normalizePapel(value: unknown): PapelEfetivoEscalaEnum {
    if (typeof value === 'number') {
      return value as PapelEfetivoEscalaEnum;
    }
    const normalized = this.normalizeText(value || '1').replace(/\s+/g, '_');
    if (normalized === 'SUPERVISOR' || normalized === '2') {
      return 2 as PapelEfetivoEscalaEnum;
    }
    if (normalized === 'VIGILANTE' || normalized === '3') {
      return 3 as PapelEfetivoEscalaEnum;
    }
    if (normalized === 'RESERVA' || normalized === '4') {
      return 4 as PapelEfetivoEscalaEnum;
    }
    return 1 as PapelEfetivoEscalaEnum;
  }

  private escalaSobrepoePeriodo(
    escala: Escala,
    periodoInicio: Date,
    periodoTermino: Date,
  ): boolean {
    const inicio = this.parseApiDate(
      (escala as any).dataInicio ?? (escala as any).dataInicioPrevista,
    );
    const termino =
      this.parseApiDate(
        (escala as any).dataTermino ??
          (escala as any).dataFim ??
          (escala as any).dataTerminoPrevista ??
          (escala as any).dataFimPrevista,
      ) ?? inicio;

    if (!inicio || !termino) return false;

    return (
      this.startOfDay(inicio) <= this.startOfDay(periodoTermino) &&
      this.startOfDay(termino) >= this.startOfDay(periodoInicio)
    );
  }

  private onlyTime(value: unknown): string {
    const match = String(value ?? '').match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '00:00';
  }

  private composeApiDate(date: string, time: string): Date {
    const dateKey = this.normalizeDateKey(date);
    return dateKey ? new Date(`${dateKey}T${time}:00`) : new Date(Number.NaN);
  }

  private parseDateKey(value: unknown): Date {
    const dateKey = this.normalizeDateKey(value);
    if (!dateKey) return new Date(Number.NaN);

    const [year, month, day] = dateKey.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    const isValid =
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day;

    return isValid ? parsed : new Date(Number.NaN);
  }

  private normalizeDateKey(value: unknown): string | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return this.toDateKey(value);
    }

    const text = String(value ?? '').trim();
    if (!text) return null;

    let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;

    match = text.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (match) return `${match[3]}-${match[2]}-${match[1]}`;

    return null;
  }

  private parseApiDate(value: unknown): Date | null {
    if (!value) return null;
    const date =
      value instanceof Date ? new Date(value) : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private toApiDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  }

  private toOptionalNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  private showError(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail,
      life: 4000,
    });
  }

  private formatDateShort(date: Date): string {
    return new Intl.DateTimeFormat('pt-AO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private getMonday(date: Date): Date {
    const result = new Date(date);
    const day = result.getDay();
    result.setDate(result.getDate() + (day === 0 ? -6 : 1 - day));
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private addDays(date: Date, amount: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    return result;
  }

  private addDaysKeepingTime(date: Date, amount: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    return result;
  }

  private endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
